// kanjo/Help.jsx — Help screen: FAQ, support form, regulatory disclaimer.
//
// Detail-mode screen reachable from the question-mark icon in the global
// header right cluster. The support form composes a mailto: to support@getkanjo.com
// (no backend round-trip required) with diagnostic context appended.

const SUPPORT_EMAIL = 'support@getkanjo.com';

const FAQ_ITEMS = [
  {
    q: "Why don't my sales here match what I rang up today?",
    a: "Sales numbers come from bank deposits, which settle a day or two behind the actual sale date and skip weekends and holidays. Treat the daily figure as a deposit snapshot, not a same-day sales total."
  },
  {
    q: 'Why is my food cost lower than what I actually spent?',
    a: "COGS only counts what's been scanned — receipts you photograph in the Receipts tab plus monthly delivery statements you upload. Anything not scanned (a cash purchase, a missed receipt) doesn't show up."
  },
  {
    q: 'How do I scan a receipt?',
    a: "Receipts → Scan, then point the camera at the invoice. Multi-page receipts can be captured one page at a time; you'll be asked to confirm the totals before saving."
  },
  {
    q: 'Why does my delivery share look off vs the platform dashboards?',
    a: "Until you upload the platform's monthly statement, Kanjo uses the bank deposits from DoorDash/Uber Eats/Grubhub — which are net of fees and tips already. Once the statement is in, the report reconciles gross sales, fees and tips line by line."
  },
  {
    q: 'Can I use these numbers for taxes or with my accountant?',
    a: "No — see About the data below. Kanjo is operational, not accounting-grade. Always reconcile with your bookkeeper at month/quarter end."
  },
  {
    q: 'How do I cancel or change my subscription?',
    a: "Account → Billing. You can change plan, update payment method, or cancel from there."
  },
];

function FaqRow({ item, open, onToggle }) {
  return (
    <div style={{ borderBottom: '1px solid var(--hairline)' }}>
      <button onClick={onToggle} aria-expanded={open} style={{
        width: '100%', background: 'transparent', border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        padding: '14px 0', textAlign: 'left',
      }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{item.q}</span>
        <span style={{ flexShrink: 0, color: 'var(--ink-3)',
          transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>
          <Chev size={14} />
        </span>
      </button>
      {open && (
        <div style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.55, padding: '0 0 14px 0' }}>
          {item.a}
        </div>
      )}
    </div>
  );
}

function SupportForm() {
  useT();
  const [kind, setKind] = React.useState('question');
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [email, setEmail] = React.useState('');
  React.useEffect(() => {
    (async () => {
      try {
        const claims = await window.kanjo?.auth?.currentClaims?.().catch(() => null);
        if (claims?.email) setEmail(claims.email);
      } catch {/* anonymous is fine */}
    })();
  }, []);

  const canSend = subject.trim().length > 0 && message.trim().length > 0;
  const onSend = () => {
    if (!canSend) return;
    const ua = (navigator.userAgent || '').slice(0, 200);
    const build = window.__KANJO_BUILD__ || 'unknown';
    const route = window.location.hash || '#/';
    const tag = kind === 'bug' ? '[Bug] ' : '[Question] ';
    const body = [
      message.trim(),
      '',
      '— sent from Kanjo —',
      `From: ${email || 'unknown'}`,
      `Build: ${build}`,
      `Route: ${route}`,
      `Agent: ${ua}`,
    ].join('\n');
    const url = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(tag + subject.trim())}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  const input = {
    width: '100%', padding: '10px 12px', borderRadius: 10,
    border: '1px solid var(--rule)', background: 'var(--surface)',
    fontSize: 14, color: 'var(--ink)', fontFamily: 'var(--sans)', boxSizing: 'border-box',
  };
  return (
    <div className="card" style={{ padding: 16 }}>
      <div role="tablist" aria-label="Message type" style={{ display: 'flex', gap: 4, padding: 3,
        background: 'var(--surface-3,#f0f0f3)', borderRadius: 8, marginBottom: 12 }}>
        {[['question', t('help.tabQuestion', 'Ask a question')], ['bug', t('help.tabBug', 'Report a bug')]].map(([k, lbl]) => {
          const on = kind === k;
          return (
            <button key={k} role="tab" aria-selected={on} onClick={() => setKind(k)}
              style={{ flex: 1, padding: '7px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
                fontSize: 12.5, fontWeight: 600,
                background: on ? 'var(--surface,#fff)' : 'transparent',
                color: on ? 'var(--ink)' : 'var(--ink-3)',
                boxShadow: on ? '0 1px 2px rgba(0,0,0,0.06)' : 'none' }}>{lbl}</button>
          );
        })}
      </div>
      <div style={{ display: 'grid', gap: 10 }}>
        <input style={input} placeholder={t('help.subject', 'Subject')} aria-label={t('help.subject', 'Subject')} value={subject} onChange={(e) => setSubject(e.target.value)} />
        <textarea style={{ ...input, minHeight: 120, resize: 'vertical', lineHeight: 1.5 }}
          placeholder={kind === 'bug'
            ? 'What did you do? What did you expect? What happened instead?'
            : 'What would you like to know?'}
          aria-label="Message" value={message} onChange={(e) => setMessage(e.target.value)} />
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 8, lineHeight: 1.5 }}>
        We'll attach your email{email ? ` (${email})` : ''}, current screen and app build to help us reproduce the issue.
      </div>
      <button onClick={onSend} disabled={!canSend} className="btn primary lg"
        style={{ width: '100%', marginTop: 14, cursor: canSend ? 'pointer' : 'default', opacity: canSend ? 1 : 0.5 }}>
        {kind === 'bug' ? t('help.sendBug', 'Send bug report') : t('help.sendQuestion', 'Send question')}
      </button>
    </div>
  );
}

function HelpScreen({ dark = false, persona = 'owner' }) {
  useT();
  const [openIdx, setOpenIdx] = React.useState(0);
  return (
    <KShell dark={dark} tab="today" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 24 }}>
        <KHeader back title={t('help.title', 'Help')} />

        <div style={{ padding: '0 20px 18px' }}>
          {/* FAQ */}
          <div className="label" style={{ marginBottom: 8, paddingLeft: 4 }}>{t('help.faqLabel', 'Frequently asked')}</div>
          <div className="card" style={{ padding: '0 16px', marginBottom: 18 }}>
            {FAQ_ITEMS.map((item, i) => (
              <FaqRow key={i} item={item} open={openIdx === i}
                onToggle={() => setOpenIdx(openIdx === i ? -1 : i)} />
            ))}
          </div>

          {/* Support form */}
          <div className="label" style={{ marginBottom: 8, paddingLeft: 4 }}>{t('help.askLabel', 'Ask a question or report a bug')}</div>
          <div style={{ marginBottom: 18 }}>
            <SupportForm />
          </div>

          {/* Regulatory disclaimer */}
          <div className="label" style={{ marginBottom: 8, paddingLeft: 4 }}>{t('help.aboutLabel', 'About the data')}</div>
          <div className="card" style={{ padding: 16, borderColor: 'var(--warning-soft,#fff4e0)' }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--warning,#9a6700)', marginBottom: 8 }}>
              {t('help.disclaimerTitle', 'Kanjo is operational — not accounting-grade')}
            </div>
            <div style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.55 }}>
              The figures shown in Kanjo are <strong>snapshots</strong> meant to help you run the business day to day.
              They do not necessarily align to accounting periods because:
            </div>
            <ul style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6, paddingLeft: 18, marginTop: 8, marginBottom: 8 }}>
              <li>Sales are read from <strong>bank deposits</strong>, which settle one to two business days after the sale and skip weekends and holidays.</li>
              <li><strong>End-of-month</strong> and quarter cutoffs rarely fall on the same day as the matching deposits, so a month's totals here can over- or under-state the books.</li>
              <li><strong>Cash deposits</strong> are made on irregular schedules and may bunch multiple days of sales into a single deposit.</li>
              <li><strong>Holidays</strong> shift settlement days and can push a deposit into the following week or month.</li>
              <li>COGS reflects only what has been <strong>scanned or imported</strong> — receipts you don't capture aren't counted.</li>
              <li>Delivery platform numbers reconcile only after the <strong>monthly statement</strong> is uploaded; before then they reflect net deposits, not gross sales.</li>
            </ul>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55 }}>
              Use these numbers to run the kitchen, not to file taxes. Reconcile with your bookkeeper or accountant at month, quarter and year end.
            </div>
          </div>
        </div>
      </div>
    </KShell>
  );
}

Object.assign(window, { HelpScreen });
